import React, { useState, useEffect } from "react";
import { Backdrop, CircularProgress, Button, Tooltip } from "@mui/material";
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import AADHeader from "../components/AADHeader.js";
import { useNavigate, useLocation } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Accordion, AccordionTab } from "primereact/accordion";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import Swal from "sweetalert2";
import SectionA from "./sections/SectionA";
import SectionB from "./sections/SectionB";
import SectionC from "./sections/SectionC";
import { collectAllSectionData } from "./utils/answerManager";
import { extractBrsrAiQuestions, applyBrsrAiAnswers } from "./utils/brsrAiQuestions";
import "./brsr.css";
import { getSavedReportName } from "../utils/getSavedReportName.js";

const steps = ["Start", "Repository", "Input", "Report"];

const BrsrModule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reportId = searchParams.get("reportId");
  const isEditMode = !!reportId;

  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Section A is active by default (0-indexed)
  // Incrementing this key forces section components to remount and re-read
  // their state from localStorage after AI fills in answers.
  const [sectionKey, setSectionKey] = useState(0);
  const [repoStatus, setRepoStatus] = useState({ ready: 0, processing: 0, total: 0, allReady: true });

  const companyid = localStorage.getItem("companyid");
  const username = localStorage.getItem("userName");
  const savedReportName = getSavedReportName();

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  // Get the reportId to use for storage (from URL or localStorage)
  const storageReportId = reportId || localStorage.getItem("brsr_report_id");

  const renderSectionContent = () => {
    switch (activeTab) {
      case 0:
        return <SectionA key={`sectionA-${sectionKey}`} reportId={storageReportId} />;
      case 1:
        return <SectionB key={`sectionB-${sectionKey}`} reportId={storageReportId} />;
      case 2:
        return <SectionC key={`sectionC-${sectionKey}`} reportId={storageReportId} />;
      default:
        return <SectionA key={`sectionA-${sectionKey}`} reportId={storageReportId} />;
    }
  };

  // ── Repository status check (same logic as CSRD RightContainer) ──────────
  const checkRepositoryStatus = async () => {
    const report_name = localStorage.getItem("report_name");
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-repository-new`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8", username },
        body: JSON.stringify({ company_id: companyid, report_name }),
      });
      if (response.ok) {
        const data = await response.json();
        const trainingStatus = data.training_status || {};
        let ready = 0, processing = 0, total = 0;
        Object.values(trainingStatus).forEach((s) => {
          total++;
          if (s.vector_upload_status === "completed" || s.vector_upload_status === "complete") ready++;
          else if (s.vector_upload_status !== "error") processing++;
        });
        const status = { ready, processing, total, allReady: processing === 0 };
        setRepoStatus(status);
        return status;
      }
    } catch (err) {
      console.error("BRSR: error checking repository status:", err);
    }
    const defaultStatus = { ready: 0, processing: 0, total: 0, allReady: true };
    setRepoStatus(defaultStatus);
    return defaultStatus;
  };

  // Poll until all files are vectorized
  useEffect(() => {
    let pollInterval = null;
    const startPolling = async () => {
      const status = await checkRepositoryStatus();
      if (!status.allReady && status.processing > 0) {
        pollInterval = setInterval(async () => {
          const next = await checkRepositoryStatus();
          if (next.allReady) { clearInterval(pollInterval); pollInterval = null; }
        }, 5000);
      }
    };
    startPolling();
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const SECTION_LABELS = ["A", "B", "C"];
  const activeSection = SECTION_LABELS[activeTab] ?? "A";

  // ── AI generation ─────────────────────────────────────────────────────────
  const generateAIAnswers = async () => {
    const report_name = localStorage.getItem("report_name");

    // Wait for section debounce timers (500ms) to flush any in-progress
    // user edits into localStorage before we read and then remount sections.
    await new Promise((r) => setTimeout(r, 600));

    const questions = extractBrsrAiQuestions(storageReportId, false, activeSection);

    if (!questions.length) {
      Swal.fire({
        icon: "info",
        title: "Nothing to generate",
        text: "All qualitative fields already have answers.",
        confirmButtonText: "OK",
      });
      return;
    }

    setAiLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3 * 60 * 1000); // 3 min

    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GenerateAnswersWithAI`, {
        method: "POST",
        headers: { "Content-type": "application/json; charset=UTF-8", username },
        signal: controller.signal,
        body: JSON.stringify({
          company_id: companyid,
          report_name,
          report_type: "brsr",
questions: questions.map((q) => q.question),
        }),
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        if (responseJson?.results?.length) {
          // Pass sentQuestions alongside results so apply uses index-based
          // matching — robust against EcoUtil rewriting question text.
          const { written, totalFields } = applyBrsrAiAnswers(storageReportId, questions, responseJson.results);
          const skipped = totalFields - written;
          // Force sections to remount so they re-read from localStorage.
          // User edits are safe — debounce was flushed above before reading.
          setSectionKey((k) => k + 1);
          Swal.fire(
            written > 0
              ? {
                  width: 400,
                  heightAuto: false,
                  icon: "success",
                  iconHtml: `<img src="/assets/img/successicon.png" style="width:55px;height:55px;" />`,
                  html: `<h4 style="color:#407775;font-size:20px;">Success!</h4>
                         <p style="font-size:16px;line-height:20px;">
                           AI filled ${written} field(s) in Section ${activeSection}${skipped > 0 ? `, ${skipped} skipped (no content found in repository)` : ""}.
                         </p>`,
                }
              : {
                  width: 400,
                  heightAuto: false,
                  html: `<div style="margin:16px 0 8px;"><img src="/assets/img/successicon.png" style="width:55px;height:55px;" /></div>
                         <h4 style="color:#407775;font-size:20px;">Partial result</h4>
                         <p style="font-size:16px;line-height:20px;">
                           AI filled ${written} field(s) in Section ${activeSection}${skipped > 0 ? `, ${skipped} skipped (no content found in repository)` : ""}.
                         </p>`,
                }
          );
        }
      } else {
        console.error("BRSR GenerateAnswersWithAI: unexpected status", res.status);
        Swal.fire({ icon: "error", title: "Error", text: `Server returned status ${res.status}. Please try again.` });
      }
    } catch (err) {
      if (err.name === "AbortError") {
        Swal.fire({ icon: "error", title: "Timed out", text: "AI generation took too long. Please try again or reduce the number of questions." });
      } else {
        console.error("BRSR GenerateAnswersWithAI error:", err);
      }
    } finally {
      clearTimeout(timeoutId);
      setAiLoading(false);
    }
  };

  const fetchData = () => {
    const questions = extractBrsrAiQuestions(storageReportId, true, activeSection); // include answered, current section only
    const hasAnswers = questions.some((q) => q.currentValue.trim());
    if (hasAnswers) {
      Swal.fire({
        icon: "warning",
        title: "Generate with AI",
        text: `Some fields in Section ${activeSection} already have answers. Generating with AI will only fill empty fields. Proceed?`,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        customClass: { confirmButton: "swal-btn", cancelButton: "swal-btn" },
      }).then((result) => {
        if (result.isConfirmed) generateAIAnswers();
      });
    } else {
      generateAIAnswers();
    }
  };

  // Handle navigation to preview - collect all section data first
  const handleNavigateToPreview = () => {
    // Collect all section data and update step4 for PDF
    collectAllSectionData(storageReportId);
    navigate(isEditMode ? `/brsr/preview-report?reportId=${encodeURIComponent(reportId)}` : "/brsr/preview-report");
  };

  return (
    <div id="wrapper">
      <div>
        {(loading || aiLoading) && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="">
            <div className="row">
              <div className="steps__containt-hader col-md-6">
                <h1 className="title">BRSR Module</h1>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/standards">
                      Reports
                    </Link>
                    <Link underline="hover" color="inherit" href="/standards">
                      Standards & Frameworks
                    </Link>
                    <Link underline="hover" color="inherit" href="/brsr/new-report">
                      BRSR
                    </Link>
                    <Typography color="text.primary">{savedReportName}</Typography>
                  </Breadcrumbs>
                </div>
              </div>
              <div className="stepper-box col-md-6">
                <Stepper activeStep={2} alternativeLabel className="brsr-stepper">
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel className="step-name">{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="button-box d-flex justify-content-end mb-4">
              <Tooltip
                title={
                  !repoStatus.allReady && repoStatus.processing > 0
                    ? `${repoStatus.processing} file(s) still analyzing. ${repoStatus.ready} of ${repoStatus.total} ready.`
                    : ""
                }
              >
                <span>
                  <Button
                    onClick={fetchData}
                    variant="outlined"
                    className="module-btn"
                    size="medium"
                    sx={{ marginRight: "15px" }}
                    disabled={aiLoading || (!repoStatus.allReady && repoStatus.processing > 0)}
                  >
                    Generate with AI
                  </Button>
                </span>
              </Tooltip>
              <Button onClick={() => navigate(isEditMode ? `/brsr/repository?reportId=${encodeURIComponent(reportId)}` : "/brsr/repository")} variant="outlined" className="module-btn" size="medium" sx={{ marginRight: "15px" }}>
                <WestIcon fontSize="small" sx={{ marginRight: "4px" }} />
                Repository
              </Button>
              <Button onClick={() => navigate("/reportDashboard")} variant="outlined" className="module-btn" size="medium" sx={{ marginRight: "15px" }} disabled>
                <img src="/assets/img/reports/Status.svg" style={{ marginRight: "4px" }} alt="Status" />
                Status
              </Button>
              <Button onClick={handleNavigateToPreview} variant="outlined" className="module-btn" size="medium">
                Report
                <EastIcon fontSize="small" sx={{ marginLeft: "4px" }} />
              </Button>
            </div>
            <div className="col-md-12">
              {/* Section Tabs */}
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="section tabs" className="brsr-section-tabs">
                <Tab
                  label={
                    <div className="tab-label-container">
                      <img src={activeTab === 0 ? "/assets/img/reports/section-a.svg" : "/assets/img/reports/section-a-inactive.svg"} alt="Section A Icon" className="tab-icon" style={{ marginRight: "8px" }} />
                      <span className={activeTab === 0 ? "tab-active" : ""}>Section A</span>
                    </div>
                  }
                />
                <Tab
                  label={
                    <div className="tab-label-container">
                      <img src={activeTab === 1 ? "/assets/img/reports/section-b.svg" : "/assets/img/reports/section-b-inactive.svg"} alt="Section B Icon" className="tab-icon" style={{ marginRight: "8px" }} />
                      <span className={activeTab === 1 ? "tab-active" : ""}>Section B</span>
                    </div>
                  }
                />
                <Tab
                  label={
                    <div className="tab-label-container">
                      <img src={activeTab === 2 ? "/assets/img/reports/section-c.svg" : "/assets/img/reports/section-c-inactive.svg"} alt="Section C Icon" className="tab-icon" style={{ marginRight: "8px" }} />
                      <span className={activeTab === 2 ? "tab-active" : ""}>Section C</span>
                    </div>
                  }
                />
              </Tabs>

              {/* Section Content */}
              <div className="brsr-section-content mt-4">{renderSectionContent()}</div>

              {/* Navigation Buttons */}
              <div className="row justify-content-end pt-3">
                <div className="col-auto">
                  <Button variant="outlined" className="outline-btn" size="medium" type="button" onClick={() => navigate(isEditMode ? `/brsr/repository?reportId=${encodeURIComponent(reportId)}` : "/brsr/repository")} startIcon={<WestIcon />}>
                    Previous
                  </Button>
                </div>
                <div className="col-auto">
                  <Button
                    className="next-button"
                    variant="contained"
                    size="medium"
                    onClick={handleNavigateToPreview}
                    endIcon={<EastIcon />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrsrModule;
